// MOBILE-OPTIMIZED VERSION - Enhanced Responsive Design

import React, { useEffect, useState } from "react";
import { MapPin, Clock, Calendar, Users, CheckCircle, Phone, IndianRupee, Ticket, AlertCircle, Loader2, MessageCircle, ChevronLeft, ChevronRight, Tag, Percent, Gift, X, UserPlus, Trash2 } from "lucide-react";
import { useNavigate } from "react-router-dom";
import toast from 'react-hot-toast';

const Booking = () => {
    const navigate = useNavigate();
    const [user, setUser] = useState(null);
    const [isLoading, setIsLoading] = useState(false);
    const [errors, setErrors] = useState({});
    const [showCustomLocation, setShowCustomLocation] = useState(false);
    const [customLocation, setCustomLocation] = useState('');
    const [showCalendar, setShowCalendar] = useState(false);
    const [currentMonth, setCurrentMonth] = useState(new Date());
    const [appliedCoupon, setAppliedCoupon] = useState(null);
    const [isCouponLoading, setIsCouponLoading] = useState(false);
    const [userBookingHistory, setUserBookingHistory] = useState(null);
    const [isCheckingHistory, setIsCheckingHistory] = useState(false);


    const API_URL = 'https://api.boomontrip.com';

    const [formData, setFormData] = useState({
        name: "",
        email: "",
        phone: "",
        date: "",
        pickup: "",
    });

    const [travelers, setTravelers] = useState([
        { id: 1, name: "", age: "", gender: "" }
    ]);

    const addTraveler = () => {
        const newId = travelers.length > 0 ? Math.max(...travelers.map(t => t.id)) + 1 : 1;
        setTravelers([...travelers, { id: newId, name: "", age: "", gender: "" }]);
    };

    const removeTraveler = (id) => {
        if (travelers.length > 1) {
            setTravelers(travelers.filter(t => t.id !== id));
            if (appliedCoupon) {
                setAppliedCoupon(null);
                toast.info('Discount removed due to traveler change. Please reapply.', {
                    duration: 2000,
                });
            }
        } else {
            toast.error('At least one traveler is required', {
                duration: 2000,
                style: {
                    border: '1px solid #ef4444',
                    padding: '16px',
                    color: '#1f2937',
                },
            });
        }
    };

    const updateTraveler = (id, field, value) => {
        setTravelers(travelers.map(t =>
            t.id === id ? { ...t, [field]: value } : t
        ));

        if (appliedCoupon) {
            setAppliedCoupon(null);
            toast.info('Discount removed due to traveler change. Please reapply.', {
                duration: 2000,
            });
        }
    };

    const getTotalTravelers = () => {
        return travelers.filter(t => t.name.trim() && t.gender).length;
    };

    const getAutoGeneratedCoupon = () => {
        const totalPeople = getTotalTravelers();

        if (totalPeople >= 4) {
            return {
                code: 'GROUP20',
                discount: 20,
                description: 'Group Offer - 20% OFF for 4+ travelers',
                type: 'group'
            };
        }

        if (totalPeople === 2) {
            return {
                code: 'COUPLE15',
                discount: 15,
                description: 'Couple Offer - 15% OFF for 2 travelers',
                type: 'couple'
            };
        }

        // Check if user has previous bookings - if yes, no first trip discount
        if (userBookingHistory && userBookingHistory.hasBookings) {
            return null; // No discount for returning customers with single traveler
        }

        return {
            code: 'FIRST10',
            discount: 10,
            description: 'First Trip Offer - 10% OFF',
            type: 'first_booking'
        };
    };


    const handleApplyCoupon = async () => {
        const validTravelers = travelers.filter(t => t.name.trim() && t.gender);

        if (validTravelers.length === 0) {
            toast.error('Please add at least one traveler with complete details', {
                duration: 3000,
                style: {
                    border: '1px solid #ef4444',
                    padding: '16px',
                    color: '#1f2937',
                },
            });
            return;
        }

        setIsCouponLoading(true);

        try {
            const autoGeneratedCoupon = getAutoGeneratedCoupon();

            // If no coupon is available (returning user with 1 traveler)
            if (!autoGeneratedCoupon) {
                toast.error('No discount available. Add more travelers for group/couple discounts!', {
                    duration: 3000,
                    style: {
                        border: '1px solid #ef4444',
                        padding: '16px',
                        color: '#1f2937',
                    },
                });
                setIsCouponLoading(false);
                return;
            }

            await new Promise(resolve => setTimeout(resolve, 500));
            setAppliedCoupon(autoGeneratedCoupon);

            toast.success(`🎉 ${autoGeneratedCoupon.description} applied!`, {
                duration: 3000,
                style: {
                    border: '1px solid #10b981',
                    padding: '16px',
                    color: '#1f2937',
                },
            });

        } catch (error) {
            console.error('Coupon application error:', error);
            toast.error('Failed to apply discount', {
                duration: 3000,
                style: {
                    border: '1px solid #ef4444',
                    padding: '16px',
                    color: '#1f2937',
                },
            });
        } finally {
            setIsCouponLoading(false);
        }
    };


    const handleRemoveCoupon = () => {
        setAppliedCoupon(null);
        toast.success('Discount removed', {
            duration: 2000,
            style: {
                border: '1px solid #10b981',
                padding: '16px',
                color: '#1f2937',
            },
        });
    };

    const checkUserBookingHistory = async (email) => {
        if (!email) return null;

        try {
            setIsCheckingHistory(true);
            const response = await fetch(`${API_URL}/api/bookings/check-history?email=${encodeURIComponent(email)}`, {
                method: 'GET',
                headers: { 'Content-Type': 'application/json' }
            });

            if (response.ok) {
                const data = await response.json();
                return data.data;
            }
            return null;
        } catch (error) {
            console.error('Error checking booking history:', error);
            return null;
        } finally {
            setIsCheckingHistory(false);
        }
    };

    useEffect(() => {
        const token = localStorage.getItem('authToken');
        const userData = localStorage.getItem('userData');

        if (token && userData) {
            try {
                const parsedUser = JSON.parse(userData);
                setUser(parsedUser);
                setFormData(prev => ({
                    ...prev,
                    name: parsedUser.name || "",
                    email: parsedUser.email || "",
                    phone: parsedUser.phone || ""
                }));

                // Automatically add logged-in user as first traveler
                setTravelers([{
                    id: 1,
                    name: parsedUser.name || "",
                    age: parsedUser.age || "",
                    gender: parsedUser.gender || ""
                }]);

                // Check if user has previous bookings
                if (parsedUser.email) {
                    checkUserBookingHistory(parsedUser.email).then(history => {
                        setUserBookingHistory(history);
                    });
                }
            } catch (err) {
                console.error("Invalid user data");
            }
        }
    }, []);


    const isWeekend = (date) => {
        const day = date.getDay();
        return day === 0 || day === 6;
    };

    const isPastDate = (date) => {
        const today = new Date();
        today.setHours(0, 0, 0, 0);
        const compareDate = new Date(date);
        compareDate.setHours(0, 0, 0, 0);
        return compareDate < today;
    };

    const getDaysInMonth = (date) => {
        const year = date.getFullYear();
        const month = date.getMonth();
        const firstDay = new Date(year, month, 1);
        const lastDay = new Date(year, month + 1, 0);
        const daysInMonth = lastDay.getDate();
        const startingDayOfWeek = firstDay.getDay();

        const days = [];

        for (let i = 0; i < startingDayOfWeek; i++) {
            days.push(null);
        }

        for (let day = 1; day <= daysInMonth; day++) {
            days.push(new Date(year, month, day));
        }

        return days;
    };

    const handleDateSelect = (date) => {
        if (!date || isPastDate(date) || !isWeekend(date)) return;

        const year = date.getFullYear();
        const month = String(date.getMonth() + 1).padStart(2, '0');
        const day = String(date.getDate()).padStart(2, '0');
        const dateString = `${year}-${month}-${day}`;

        setFormData(prev => ({ ...prev, date: dateString }));
        setShowCalendar(false);

        if (errors.date) {
            setErrors(prev => ({ ...prev, date: "" }));
        }
    };

    const handlePrevMonth = () => {
        setCurrentMonth(new Date(currentMonth.getFullYear(), currentMonth.getMonth() - 1));
    };

    const handleNextMonth = () => {
        setCurrentMonth(new Date(currentMonth.getFullYear(), currentMonth.getMonth() + 1));
    };

    const handleInputChange = (e) => {
        const { name, value } = e.target;
        setFormData({
            ...formData,
            [name]: value
        });

        if (errors[name]) {
            setErrors(prev => ({ ...prev, [name]: "" }));
        }
    };

    const handlePickupChange = (e) => {
        const value = e.target.value;

        if (value === "Other") {
            setShowCustomLocation(true);
            setFormData({
                ...formData,
                pickup: "Other"
            });
        } else {
            setShowCustomLocation(false);
            setCustomLocation('');
            setFormData({
                ...formData,
                pickup: value
            });
        }

        if (errors.pickup) {
            setErrors(prev => ({ ...prev, pickup: "" }));
        }
    };

    const handleCustomLocationChange = (e) => {
        const value = e.target.value;
        setCustomLocation(value);

        if (errors.pickup && value.length >= 4) {
            setErrors(prev => ({ ...prev, pickup: "" }));
        }
    };

    const handleWhatsAppContact = () => {
        const message = `Hi, I would like to book a tour with pickup from: ${customLocation}`;
        const whatsappUrl = `https://wa.me/917398747222?text=${encodeURIComponent(message)}`;
        window.open(whatsappUrl, '_blank');
    };

    const validateForm = () => {
        const newErrors = {};

        if (!formData.name.trim()) {
            newErrors.name = "Name is required";
        }

        if (!formData.email.trim()) {
            newErrors.email = "Email is required";
        } else if (!/\S+@\S+\.\S+/.test(formData.email)) {
            newErrors.email = "Invalid email format";
        }

        if (!formData.phone.trim()) {
            newErrors.phone = "Phone number is required";
        } else if (!/^\d{10}$/.test(formData.phone.replace(/\s/g, ''))) {
            newErrors.phone = "Invalid phone number (10 digits required)";
        }

        if (!formData.date) {
            newErrors.date = "Tour date is required";
        } else {
            const selectedDate = new Date(formData.date);
            const today = new Date();
            today.setHours(0, 0, 0, 0);

            if (selectedDate < today) {
                newErrors.date = "Date cannot be in the past";
            } else {
                const dayOfWeek = selectedDate.getDay();
                if (dayOfWeek !== 0 && dayOfWeek !== 6) {
                    newErrors.date = "Tours are only available on Saturdays and Sundays";
                }
            }
        }

        const validTravelers = travelers.filter(t => t.name.trim() && t.gender);

        if (validTravelers.length === 0) {
            newErrors.travelers = "At least one complete traveler information is required";
        }

        if (!formData.pickup) {
            newErrors.pickup = "Please select a pickup location";
        } else if (formData.pickup === "Other" && !customLocation.trim()) {
            newErrors.pickup = "Please enter your pickup location";
        } else if (formData.pickup === "Other" && customLocation.trim().length < 5) {
            newErrors.pickup = "Pickup location must be at least 5 characters";
        }

        setErrors(newErrors);
        return Object.keys(newErrors).length === 0;
    };

    const handleSubmit = async () => {
        if (!validateForm()) {
            toast.error("Please fill all required fields correctly", {
                duration: 3000,
                style: {
                    border: '1px solid #ef4444',
                    padding: '16px',
                    color: '#1f2937',
                },
            });
            return;
        }

        setIsLoading(true);

        try {
            const totalTravelers = getTotalTravelers();
            const basePrice = totalTravelers * 2199;

            const discountAmount = appliedCoupon
                ? Math.round((basePrice * appliedCoupon.discount) / 100)
                : 0;

            const finalPrice = basePrice - discountAmount;

            const bookingData = {
                name: formData.name.trim(),
                email: formData.email.trim(),
                phone: formData.phone.trim(),
                date: formData.date,
                adults: totalTravelers,
                children: 0,
                pickup: formData.pickup,
                totalPrice: finalPrice,
                originalPrice: basePrice,
                travelers: travelers.filter(t => t.name.trim() && t.gender)
            };

            if (appliedCoupon) {
                bookingData.discount = {
                    code: appliedCoupon.code,
                    type: appliedCoupon.type,
                    percent: appliedCoupon.discount,
                    amount: discountAmount
                };
            }

            if (formData.pickup === "Other") {
                bookingData.customPickupLocation = customLocation.trim();
            }

            console.log('📦 Booking Data:', bookingData);

            const bookingResponse = await fetch('https://api.boomontrip.com/api/bookings', {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify(bookingData)
            });

            const responseData = await bookingResponse.json();

            if (!bookingResponse.ok) {
                const errorMessage = responseData.errors && responseData.errors.length > 0
                    ? responseData.errors.join(', ')
                    : responseData.message || 'Booking request failed';
                throw new Error(errorMessage);
            }

            if (!responseData.success) {
                throw new Error(responseData.message || 'Booking failed');
            }

            sessionStorage.setItem('currentBooking', JSON.stringify(responseData.data.booking));

            const paymentResponse = await fetch('https://api.boomontrip.com/api/payments/initiate', {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({
                    bookingId: responseData.data.booking._id,
                    paymentMethod: 'upi'
                })
            });

            if (!paymentResponse.ok) {
                const errorData = await paymentResponse.json();
                throw new Error(errorData.message || 'Payment initiation failed');
            }

            const paymentData = await paymentResponse.json();

            if (!paymentData.success) {
                throw new Error(paymentData.message || 'Payment initiation failed');
            }

            sessionStorage.setItem('currentPayment', JSON.stringify(paymentData.data));

            toast.success('Booking created! Redirecting to payment...', {
                duration: 2000,
                style: {
                    border: '1px solid #10b981',
                    padding: '16px',
                    color: '#1f2937',
                },
            });

            setTimeout(() => {
                navigate('/payment');
            }, 1000);

        } catch (error) {
            console.error('Booking submission error:', error);
            toast.error(error.message || 'Something went wrong. Please try again.', {
                duration: 4000,
                style: {
                    border: '1px solid #ef4444',
                    padding: '16px',
                    color: '#1f2937',
                },
            });
            setIsLoading(false);
        }
    };

    const totalTravelers = getTotalTravelers();
    const basePrice = totalTravelers * 2199;
    const discountAmount = appliedCoupon ? Math.round((basePrice * appliedCoupon.discount) / 100) : 0;
    const totalPrice = basePrice - discountAmount;

    const pickupLocations = [
        { value: 'Bellandur - 07:00 AM', name: 'Bellandur', time: '07:00 AM' },
        { value: 'Marathahalli (Kalamandir) - 07:15 AM', name: 'Marathahalli (Kalamandir)', time: '07:15 AM' },
        { value: 'Spice Garden, Marathahalli - 07:30 AM', name: 'Spice Garden, Marathahalli', time: '07:30 AM' }
    ];

    const monthNames = ["January", "February", "March", "April", "May", "June",
        "July", "August", "September", "October", "November", "December"
    ];

    const dayNames = ["Sun", "Mon", "Tue", "Wed", "Thu", "Fri", "Sat"];

    return (
        <div className="min-h-screen bg-gradient-to-br from-orange-50 via-red-50 to-yellow-50 py-4 sm:py-8">
            <div className="max-w-5xl mx-auto px-3 sm:px-4 lg:px-6">
                {/* Header */}
                <div className="text-center mb-6 sm:mb-8">
                    <h1 className="text-xl sm:text-3xl lg:text-4xl font-bold text-gray-900 mb-2 sm:mb-3 px-2">
                        Complete Your Booking
                    </h1>
                    <p className="text-gray-600 text-xs sm:text-sm lg:text-base px-4">
                        You're just a few steps away from an amazing Bangalore tour experience
                    </p>
                </div>

                {/* Applied Discount Banner */}
                {appliedCoupon && (
                    <div className="mb-4 sm:mb-6 bg-gradient-to-r from-green-600 to-emerald-500 rounded-lg sm:rounded-xl p-3 sm:p-4 shadow-lg animate-fadeIn">
                        <div className="flex items-center gap-2 sm:gap-3 text-white">
                            <div className="bg-white/20 p-1.5 sm:p-2 rounded-full flex-shrink-0">
                                <Gift className="w-4 h-4 sm:w-5 sm:h-5" />
                            </div>
                            <div className="flex-1 min-w-0">
                                <p className="font-bold text-xs sm:text-sm lg:text-base leading-tight">
                                    "{appliedCoupon.code}" Applied - {appliedCoupon.discount}% OFF!
                                </p>
                                <p className="text-[10px] sm:text-xs text-green-50 mt-0.5">
                                    {appliedCoupon.description} • Save ₹{discountAmount.toLocaleString('en-IN')}
                                </p>
                            </div>
                            <button
                                onClick={handleRemoveCoupon}
                                className="bg-white/20 hover:bg-white/30 p-1.5 sm:p-2 rounded-lg transition-colors flex-shrink-0"
                            >
                                <X className="w-3.5 h-3.5 sm:w-4 sm:h-4" />
                            </button>
                        </div>
                    </div>
                )}

                <div className="grid grid-cols-1 lg:grid-cols-3 gap-4 sm:gap-6">
                    {/* Main Form */}
                    <div className="lg:col-span-2">
                        <div className="bg-white rounded-lg sm:rounded-2xl shadow-lg sm:shadow-xl p-4 sm:p-6 lg:p-8">
                            <h2 className="text-lg sm:text-xl lg:text-2xl font-bold text-gray-900 mb-4 sm:mb-6 flex items-center gap-2">
                                <Users className="w-5 h-5 sm:w-6 sm:h-6 text-orange-600" />
                                Booking Details
                            </h2>

                            <div className="space-y-4 sm:space-y-5">
                                {/* Name */}
                                <div>
                                    <label className="block text-xs sm:text-sm font-semibold text-gray-700 mb-1.5 sm:mb-2">
                                        Full Name <span className="text-red-500">*</span>
                                    </label>
                                    <input
                                        type="text"
                                        name="name"
                                        value={formData.name}
                                        onChange={handleInputChange}
                                        className={`w-full px-3 sm:px-4 py-2.5 sm:py-3 rounded-lg sm:rounded-xl border ${errors.name ? 'border-red-500' : 'border-gray-300'} focus:ring-2 focus:ring-orange-500 focus:border-transparent outline-none transition-all text-sm sm:text-base`}
                                        placeholder="Enter your full name"
                                    />
                                    {errors.name && (
                                        <p className="text-red-500 text-xs sm:text-sm mt-1 flex items-center gap-1">
                                            <AlertCircle className="w-3 h-3 sm:w-4 sm:h-4" />
                                            {errors.name}
                                        </p>
                                    )}
                                </div>

                                {/* Email & Phone */}
                                <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 sm:gap-5">
                                    <div>
                                        <label className="block text-xs sm:text-sm font-semibold text-gray-700 mb-1.5 sm:mb-2">
                                            Email <span className="text-red-500">*</span>
                                        </label>
                                        <input
                                            type="email"
                                            name="email"
                                            value={formData.email}
                                            onChange={handleInputChange}
                                            className={`w-full px-3 sm:px-4 py-2.5 sm:py-3 rounded-lg sm:rounded-xl border ${errors.email ? 'border-red-500' : 'border-gray-300'} focus:ring-2 focus:ring-orange-500 focus:border-transparent outline-none transition-all text-sm sm:text-base`}
                                            placeholder="your@email.com"
                                        />
                                        {errors.email && (
                                            <p className="text-red-500 text-xs sm:text-sm mt-1 flex items-center gap-1">
                                                <AlertCircle className="w-3 h-3 sm:w-4 sm:h-4" />
                                                {errors.email}
                                            </p>
                                        )}
                                    </div>

                                    <div>
                                        <label className="block text-xs sm:text-sm font-semibold text-gray-700 mb-1.5 sm:mb-2">
                                            Phone <span className="text-red-500">*</span>
                                        </label>
                                        <input
                                            type="tel"
                                            name="phone"
                                            maxLength={10}
                                            value={formData.phone}
                                            onChange={handleInputChange}
                                            className={`w-full px-3 sm:px-4 py-2.5 sm:py-3 rounded-lg sm:rounded-xl border ${errors.phone ? 'border-red-500' : 'border-gray-300'} focus:ring-2 focus:ring-orange-500 focus:border-transparent outline-none transition-all text-sm sm:text-base`}
                                            placeholder="10-digit number"
                                        />
                                        {errors.phone && (
                                            <p className="text-red-500 text-xs sm:text-sm mt-1 flex items-center gap-1">
                                                <AlertCircle className="w-3 h-3 sm:w-4 sm:h-4" />
                                                {errors.phone}
                                            </p>
                                        )}
                                    </div>
                                </div>

                                {/* Tour Date */}
                                <div>
                                    <label className="block text-xs sm:text-sm font-semibold text-gray-700 mb-1.5 sm:mb-2">
                                        Tour Date <span className="text-red-500">*</span>
                                    </label>
                                    <div className="bg-blue-50 border border-blue-200 rounded-lg p-2.5 sm:p-3 mb-2 sm:mb-3">
                                        <div className="flex gap-2">
                                            <AlertCircle className="w-3.5 h-3.5 sm:w-4 sm:h-4 text-blue-600 flex-shrink-0 mt-0.5" />
                                            <p className="text-[10px] sm:text-xs text-gray-700 leading-relaxed">
                                                <span className="font-semibold text-gray-900">Tours on Saturdays & Sundays only.</span> Weekends in green.
                                            </p>
                                        </div>
                                    </div>

                                    <div className="relative">
                                        <div
                                            onClick={() => setShowCalendar(!showCalendar)}
                                            className={`w-full px-3 sm:px-4 py-2.5 sm:py-3 rounded-lg sm:rounded-xl border ${errors.date ? 'border-red-500' : 'border-gray-300'} cursor-pointer bg-white flex items-center justify-between hover:border-orange-400 transition-colors`}
                                        >
                                            <span className={`text-sm sm:text-base ${formData.date ? 'text-gray-900' : 'text-gray-400'}`}>
                                                {formData.date
                                                    ? new Date(formData.date).toLocaleDateString('en-IN', {
                                                        weekday: 'short',
                                                        month: 'short',
                                                        day: 'numeric',
                                                        year: 'numeric'
                                                    })
                                                    : 'Select a date'
                                                }
                                            </span>
                                            <Calendar className="w-4 h-4 sm:w-5 sm:h-5 text-gray-400" />
                                        </div>

                                        {showCalendar && (
                                            <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/50 sm:absolute sm:inset-auto sm:mt-2 sm:bg-transparent">
                                                <div className="w-full max-w-sm sm:max-w-none bg-white rounded-xl shadow-2xl border border-gray-200 p-4">
                                                    <div className="flex items-center justify-between mb-4">
                                                        <button
                                                            type="button"
                                                            onClick={handlePrevMonth}
                                                            className="p-2 hover:bg-gray-100 rounded-lg transition-colors"
                                                        >
                                                            <ChevronLeft className="w-4 h-4 sm:w-5 sm:h-5 text-gray-600" />
                                                        </button>
                                                        <h3 className="font-bold text-sm sm:text-base text-gray-900">
                                                            {monthNames[currentMonth.getMonth()]} {currentMonth.getFullYear()}
                                                        </h3>
                                                        <button
                                                            type="button"
                                                            onClick={handleNextMonth}
                                                            className="p-2 hover:bg-gray-100 rounded-lg transition-colors"
                                                        >
                                                            <ChevronRight className="w-4 h-4 sm:w-5 sm:h-5 text-gray-600" />
                                                        </button>
                                                    </div>

                                                    <div className="grid grid-cols-7 gap-1 mb-2">
                                                        {dayNames.map(day => (
                                                            <div key={day} className="text-center text-[10px] sm:text-xs font-semibold text-gray-600 py-1.5 sm:py-2">
                                                                {day}
                                                            </div>
                                                        ))}
                                                    </div>

                                                    <div className="grid grid-cols-7 gap-1">
                                                        {getDaysInMonth(currentMonth).map((date, index) => {
                                                            if (!date) {
                                                                return <div key={`empty-${index}`} className="aspect-square" />;
                                                            }

                                                            const year = date.getFullYear();
                                                            const month = String(date.getMonth() + 1).padStart(2, '0');
                                                            const day = String(date.getDate()).padStart(2, '0');
                                                            const dateString = `${year}-${month}-${day}`;

                                                            const isSelected = formData.date === dateString;
                                                            const isWeekendDay = isWeekend(date);
                                                            const isPast = isPastDate(date);
                                                            const isDisabled = isPast || !isWeekendDay;

                                                            return (
                                                                <button
                                                                    key={date.toISOString()}
                                                                    type="button"
                                                                    onClick={() => handleDateSelect(date)}
                                                                    disabled={isDisabled}
                                                                    className={`aspect-square p-1 sm:p-2 rounded-md sm:rounded-lg text-xs sm:text-sm font-medium transition-all ${isSelected
                                                                        ? 'bg-orange-600 text-white shadow-lg'
                                                                        : isWeekendDay && !isPast
                                                                            ? 'bg-green-100 text-green-900 hover:bg-green-200 border-2 border-green-300'
                                                                            : isPast
                                                                                ? 'text-gray-300 cursor-not-allowed'
                                                                                : 'text-gray-400 cursor-not-allowed bg-gray-50'
                                                                        }`}
                                                                >
                                                                    {date.getDate()}
                                                                </button>
                                                            );
                                                        })}
                                                    </div>

                                                    <div className="mt-4 pt-4 border-t border-gray-200">
                                                        <button
                                                            type="button"
                                                            onClick={() => setShowCalendar(false)}
                                                            className="w-full py-2.5 sm:py-2 bg-orange-600 text-white rounded-lg font-semibold hover:bg-orange-700 transition-colors text-sm sm:text-base"
                                                        >
                                                            Close
                                                        </button>
                                                    </div>
                                                </div>
                                            </div>
                                        )}
                                    </div>

                                    {errors.date && (
                                        <p className="text-red-500 text-xs sm:text-sm mt-1 flex items-center gap-1">
                                            <AlertCircle className="w-3 h-3 sm:w-4 sm:h-4" />
                                            {errors.date}
                                        </p>
                                    )}
                                </div>

                                {/* Travelers Section */}
                                <div className="bg-gradient-to-r from-blue-50 to-indigo-50 rounded-lg sm:rounded-xl p-3 sm:p-4 border border-blue-200">
                                    <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3 sm:gap-0 mb-3 sm:mb-4">
                                        <div className="flex items-center gap-2">
                                            <Users className="w-4 h-4 sm:w-5 sm:h-5 text-blue-600" />
                                            <h3 className="font-bold text-sm sm:text-base text-gray-900">Add Travelers</h3>
                                        </div>
                                        <button
                                            type="button"
                                            onClick={addTraveler}
                                            className="bg-blue-600 hover:bg-blue-700 text-white px-3 py-2 rounded-lg text-xs sm:text-sm font-semibold flex items-center justify-center gap-2 transition-colors w-full sm:w-auto"
                                        >
                                            <UserPlus className="w-3.5 h-3.5 sm:w-4 sm:h-4" />
                                            Add Traveler
                                        </button>
                                    </div>

                                    <p className="text-[10px] sm:text-xs text-gray-600 mb-3 sm:mb-4">
                                        ₹2,199 per person
                                    </p>

                                    <div className="space-y-3 sm:space-y-4">
                                        {travelers.map((traveler, index) => (
                                            <div key={traveler.id} className="bg-white rounded-lg p-3 sm:p-4 border-2 border-blue-200">
                                                <div className="flex items-center justify-between mb-2 sm:mb-3">
                                                    <h4 className="font-semibold text-xs sm:text-sm text-gray-900">Traveler {index + 1}</h4>
                                                    {travelers.length > 1 && (
                                                        <button
                                                            type="button"
                                                            onClick={() => removeTraveler(traveler.id)}
                                                            className="text-red-600 hover:text-red-700 p-1"
                                                        >
                                                            <Trash2 className="w-3.5 h-3.5 sm:w-4 sm:h-4" />
                                                        </button>
                                                    )}
                                                </div>
                                                <div className="grid grid-cols-2 gap-2 sm:gap-3">
                                                    <div>
                                                        <label className="block text-[10px] sm:text-xs font-semibold text-gray-700 mb-1">
                                                            Name
                                                        </label>
                                                        <input
                                                            type="text"
                                                            value={traveler.name}
                                                            onChange={(e) => updateTraveler(traveler.id, 'name', e.target.value)}
                                                            className="w-full px-2.5 sm:px-3 py-2 rounded-lg border border-gray-300 focus:ring-2 focus:ring-blue-500 focus:border-transparent outline-none text-xs sm:text-sm"
                                                            placeholder="Full name"
                                                        />
                                                    </div>
                                                    <div>
                                                        <label className="block text-[10px] sm:text-xs font-semibold text-gray-700 mb-1">
                                                            Gender
                                                        </label>
                                                        <select
                                                            value={traveler.gender}
                                                            onChange={(e) => updateTraveler(traveler.id, 'gender', e.target.value)}
                                                            className="w-full px-2.5 sm:px-3 py-2 rounded-lg border border-gray-300 focus:ring-2 focus:ring-blue-500 focus:border-transparent outline-none text-xs sm:text-sm"
                                                        >
                                                            <option value="">Select</option>
                                                            <option value="Male">Male</option>
                                                            <option value="Female">Female</option>
                                                            <option value="Other">Other</option>
                                                        </select>
                                                    </div>
                                                </div>
                                            </div>
                                        ))}
                                    </div>

                                    {errors.travelers && (
                                        <p className="text-red-500 text-xs sm:text-sm mt-2 flex items-center gap-1">
                                            <AlertCircle className="w-3 h-3 sm:w-4 sm:h-4" />
                                            {errors.travelers}
                                        </p>
                                    )}
                                </div>

                                {/* Coupon Section */}
                                <div className="bg-gradient-to-r from-purple-50 to-pink-50 rounded-lg sm:rounded-xl p-3 sm:p-4 border border-purple-200">
                                    <div className="flex items-center gap-2 mb-2 sm:mb-3">
                                        <Gift className="w-4 h-4 sm:w-5 sm:h-5 text-purple-600" />
                                        <h3 className="font-bold text-sm sm:text-base text-gray-900">Get Your Discount!</h3>
                                    </div>

                                    <p className="text-[10px] sm:text-xs text-gray-600 mb-2 sm:mb-3">
                                        Click "Apply Discount" to get the best offer
                                    </p>

                                    {!appliedCoupon && (
                                        <div className="bg-white border-2 border-purple-200 rounded-lg p-2.5 sm:p-3 mb-2 sm:mb-3">
                                            <p className="text-xs sm:text-sm font-semibold text-gray-900 mb-1.5 sm:mb-2">
                                                Available Discount:
                                            </p>
                                            {(() => {
                                                const totalPeople = getTotalTravelers();

                                                if (totalPeople === 0) {
                                                    return (
                                                        <p className="text-[10px] sm:text-xs text-gray-500">
                                                            Add travelers to see discounts
                                                        </p>
                                                    );
                                                }

                                                if (totalPeople >= 4) {
                                                    return (
                                                        <div className="bg-green-50 border border-green-200 rounded p-2">
                                                            <p className="text-xs sm:text-sm font-bold text-green-900">🎉 Group - 20% OFF</p>
                                                            <p className="text-[10px] sm:text-xs text-green-700">{totalPeople} travelers • GROUP20</p>
                                                        </div>
                                                    );
                                                }

                                                if (totalPeople === 2) {
                                                    return (
                                                        <div className="bg-pink-50 border border-pink-200 rounded p-2">
                                                            <p className="text-xs sm:text-sm font-bold text-pink-900">💑 Couple - 15% OFF</p>
                                                            <p className="text-[10px] sm:text-xs text-pink-700">2 travelers • COUPLE15</p>
                                                        </div>
                                                    );
                                                }

                                                // Check if user has previous bookings
                                                if (userBookingHistory && userBookingHistory.hasBookings) {
                                                    return (
                                                        <div className="bg-gray-50 border border-gray-200 rounded p-2">
                                                            <p className="text-xs sm:text-sm font-bold text-gray-900">Welcome Back! 🎊</p>
                                                            <p className="text-[10px] sm:text-xs text-gray-600">
                                                                Add more travelers for couple/group discounts
                                                            </p>
                                                        </div>
                                                    );
                                                }

                                                return (
                                                    <div className="bg-blue-50 border border-blue-200 rounded p-2">
                                                        <p className="text-xs sm:text-sm font-bold text-blue-900">🎁 First Trip - 10% OFF</p>
                                                        <p className="text-[10px] sm:text-xs text-blue-700">{totalPeople} traveler{totalPeople > 1 ? 's' : ''} • FIRST10</p>
                                                    </div>
                                                );
                                            })()}
                                        </div>
                                    )}

                                    {!appliedCoupon ? (
                                        <button
                                            onClick={handleApplyCoupon}
                                            disabled={isCouponLoading}
                                            className="w-full bg-gradient-to-r from-purple-600 to-pink-600 hover:from-purple-700 hover:to-pink-700 text-white px-3 sm:px-4 py-2.5 sm:py-3 rounded-lg font-semibold text-xs sm:text-sm transition-colors disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center gap-2"
                                        >
                                            {isCouponLoading ? (
                                                <>
                                                    <Loader2 className="w-3.5 h-3.5 sm:w-4 sm:h-4 animate-spin" />
                                                    Applying...
                                                </>
                                            ) : (
                                                <>
                                                    <Gift className="w-3.5 h-3.5 sm:w-4 sm:h-4" />
                                                    Apply Discount
                                                </>
                                            )}
                                        </button>
                                    ) : (
                                        <div className="bg-gradient-to-r from-green-100 to-emerald-100 border-2 border-green-500 rounded-lg p-3 sm:p-4">
                                            <div className="flex items-start justify-between gap-2 sm:gap-3">
                                                <div className="flex-1 min-w-0">
                                                    <div className="flex items-center gap-1.5 sm:gap-2 mb-1.5 sm:mb-2">
                                                        <div className="bg-green-600 text-white px-1.5 sm:px-2 py-0.5 sm:py-1 rounded text-[10px] sm:text-xs font-bold">
                                                            {appliedCoupon.code}
                                                        </div>
                                                        <CheckCircle className="w-4 h-4 sm:w-5 sm:h-5 text-green-600" />
                                                    </div>
                                                    <p className="font-bold text-green-900 text-xs sm:text-sm mb-0.5 sm:mb-1">
                                                        {appliedCoupon.discount}% Discount Applied!
                                                    </p>
                                                    <p className="text-[10px] sm:text-xs text-green-700">
                                                        {appliedCoupon.description}
                                                    </p>
                                                    <p className="text-[10px] sm:text-xs text-green-600 font-semibold mt-1 sm:mt-2">
                                                        Save ₹{discountAmount.toLocaleString('en-IN')}
                                                    </p>
                                                </div>
                                                <button
                                                    onClick={handleRemoveCoupon}
                                                    className="bg-red-100 hover:bg-red-200 text-red-700 p-1.5 sm:p-2 rounded-lg transition-colors flex-shrink-0"
                                                >
                                                    <X className="w-3 h-3 sm:w-4 sm:h-4" />
                                                </button>
                                            </div>
                                        </div>
                                    )}
                                </div>

                                {/* Available Discounts Info */}
                                <div className="bg-gradient-to-r from-orange-50 to-red-50 rounded-lg sm:rounded-xl p-3 sm:p-4 border border-orange-200">
                                    <div className="flex items-center gap-2 mb-2 sm:mb-3">
                                        <Percent className="w-4 h-4 sm:w-5 sm:h-5 text-orange-600" />
                                        <h3 className="font-bold text-xs sm:text-sm lg:text-base text-gray-900">All Discounts</h3>
                                    </div>
                                    <div className="grid grid-cols-3 gap-2 sm:gap-3">
                                        <div className={`p-2 sm:p-3 rounded-lg border-2 ${appliedCoupon?.type === 'couple' ? 'bg-green-100 border-green-500' : 'bg-white border-orange-200'}`}>
                                            <p className="font-bold text-[10px] sm:text-xs lg:text-sm text-gray-900">💑 Couple</p>
                                            <p className="text-[9px] sm:text-[10px] lg:text-xs text-gray-600">15% off</p>
                                            {appliedCoupon?.type === 'couple' && (
                                                <p className="text-[9px] sm:text-xs text-green-600 font-semibold mt-0.5 sm:mt-1">✓ Active</p>
                                            )}
                                        </div>
                                        <div className={`p-2 sm:p-3 rounded-lg border-2 ${appliedCoupon?.type === 'first_booking' ? 'bg-green-100 border-green-500' : 'bg-white border-orange-200'}`}>
                                            <p className="font-bold text-[10px] sm:text-xs lg:text-sm text-gray-900">🎁 First Trip</p>
                                            <p className="text-[9px] sm:text-[10px] lg:text-xs text-gray-600">10% off</p>
                                            {appliedCoupon?.type === 'first_booking' && (
                                                <p className="text-[9px] sm:text-xs text-green-600 font-semibold mt-0.5 sm:mt-1">✓ Active</p>
                                            )}
                                        </div>
                                        <div className={`p-2 sm:p-3 rounded-lg border-2 ${appliedCoupon?.type === 'group' ? 'bg-green-100 border-green-500' : 'bg-white border-orange-200'}`}>
                                            <p className="font-bold text-[10px] sm:text-xs lg:text-sm text-gray-900">🎉 Group</p>
                                            <p className="text-[9px] sm:text-[10px] lg:text-xs text-gray-600">20% off</p>
                                            {appliedCoupon?.type === 'group' && (
                                                <p className="text-[9px] sm:text-xs text-green-600 font-semibold mt-0.5 sm:mt-1">✓ Active</p>
                                            )}
                                        </div>
                                    </div>
                                </div>

                                {/* Pickup Location */}
                                <div>
                                    <label className="block text-xs sm:text-sm font-semibold text-gray-700 mb-1.5 sm:mb-2">
                                        Pickup Location <span className="text-red-500">*</span>
                                    </label>
                                    <p className="text-[10px] sm:text-xs text-gray-600 mb-2 sm:mb-3">
                                        Select your pickup point or choose "Other"
                                    </p>

                                    <div className="bg-blue-50 border border-blue-200 rounded-lg p-2.5 sm:p-3 mb-3 sm:mb-4">
                                        <div className="flex gap-2">
                                            <AlertCircle className="w-3.5 h-3.5 sm:w-4 sm:h-4 text-blue-600 flex-shrink-0 mt-0.5" />
                                            <div className="text-[10px] sm:text-xs text-gray-700">
                                                <p className="font-semibold text-gray-900 mb-1">Note:</p>
                                                <p>Pickup confirmed if location is on route</p>
                                            </div>
                                        </div>
                                    </div>

                                    <div className="space-y-2 sm:space-y-3">
                                        {pickupLocations.map((location, index) => (
                                            <label
                                                key={index}
                                                className={`flex items-center justify-between p-3 sm:p-4 cursor-pointer transition-all rounded-lg ${formData.pickup === location.value && !showCustomLocation
                                                    ? 'bg-orange-100 border-2 border-orange-500'
                                                    : 'bg-orange-50 border-2 border-transparent hover:border-orange-300'
                                                    }`}
                                            >
                                                <div className="flex items-center gap-2 sm:gap-3 flex-1">
                                                    <input
                                                        type="radio"
                                                        name="pickup"
                                                        value={location.value}
                                                        checked={formData.pickup === location.value && !showCustomLocation}
                                                        onChange={handlePickupChange}
                                                        className="w-3.5 h-3.5 sm:w-4 sm:h-4 text-orange-600 focus:ring-orange-500"
                                                    />
                                                    <div className="flex-1">
                                                        <div className="font-bold text-xs sm:text-sm text-gray-900 flex items-center gap-1.5 sm:gap-2">
                                                            <MapPin className="w-3 h-3 sm:w-4 sm:h-4 text-orange-600" />
                                                            {location.name}
                                                        </div>
                                                    </div>
                                                </div>
                                                <div className="flex items-center gap-1 sm:gap-2 text-orange-600 font-semibold text-[10px] sm:text-xs">
                                                    <Clock className="w-3 h-3 sm:w-4 sm:h-4" />
                                                    {location.time}
                                                </div>
                                            </label>
                                        ))}

                                        <label
                                            className={`flex items-start p-3 sm:p-4 cursor-pointer transition-all rounded-lg ${showCustomLocation
                                                ? 'bg-orange-100 border-2 border-orange-500'
                                                : 'bg-orange-50 border-2 border-transparent hover:border-orange-300'
                                                }`}
                                        >
                                            <div className="w-full flex items-start gap-2 sm:gap-3">
                                                <input
                                                    type="radio"
                                                    name="pickup"
                                                    value="Other"
                                                    checked={showCustomLocation}
                                                    onChange={handlePickupChange}
                                                    className="w-3.5 h-3.5 sm:w-4 sm:h-4 text-orange-600 focus:ring-orange-500 mt-1"
                                                />
                                                <div className="flex-1">
                                                    <div className="font-bold text-xs sm:text-sm text-gray-900 mb-2 flex items-center gap-1.5 sm:gap-2">
                                                        <MapPin className="w-3 h-3 sm:w-4 sm:h-4 text-orange-600" />
                                                        Other Location
                                                    </div>
                                                    {showCustomLocation && (
                                                        <input
                                                            type="text"
                                                            value={customLocation}
                                                            onChange={handleCustomLocationChange}
                                                            placeholder="Enter your location (min. 5 chars)"
                                                            className={`w-full px-3 sm:px-4 py-2 border ${errors.pickup ? 'border-red-500' : 'border-gray-300'} rounded-lg focus:outline-none focus:ring-2 focus:ring-orange-500 focus:border-transparent text-xs sm:text-sm`}
                                                        />
                                                    )}
                                                </div>
                                            </div>
                                        </label>
                                    </div>

                                    {showCustomLocation && customLocation && customLocation.trim().length >= 5 && (
                                        <div className="bg-green-50 border border-green-200 rounded-lg p-3 sm:p-4 flex gap-2 sm:gap-3 mt-3 animate-fadeIn">
                                            <MessageCircle className="w-4 h-4 sm:w-5 sm:h-5 text-green-600 flex-shrink-0 mt-0.5" />
                                            <div className="flex-1">
                                                <p className="font-semibold text-gray-900 mb-1 text-xs sm:text-sm">
                                                    WhatsApp Confirmation
                                                </p>
                                                <p className="text-[10px] sm:text-xs text-gray-700 mb-2 sm:mb-3">
                                                    Pickup arranged if on route
                                                </p>
                                                <button
                                                    type="button"
                                                    onClick={handleWhatsAppContact}
                                                    className="bg-green-600 hover:bg-green-700 text-white px-3 sm:px-4 py-2 rounded-lg text-xs sm:text-sm font-semibold flex items-center gap-2 transition-colors w-full sm:w-auto justify-center"
                                                >
                                                    Contact on WhatsApp
                                                </button>
                                            </div>
                                        </div>
                                    )}

                                    {errors.pickup && (
                                        <p className="text-red-500 text-xs sm:text-sm mt-2 flex items-center gap-1">
                                            <AlertCircle className="w-3 h-3 sm:w-4 sm:h-4" />
                                            {errors.pickup}
                                        </p>
                                    )}
                                </div>
                            </div>
                        </div>
                    </div>

                    {/* Booking Summary Sidebar */}
                    <div className="lg:col-span-1">
                        <div className="bg-white rounded-lg sm:rounded-2xl shadow-lg sm:shadow-xl p-4 sm:p-6 lg:sticky lg:top-4">
                            <h3 className="text-base sm:text-lg lg:text-xl font-bold text-gray-900 mb-3 sm:mb-4">Summary</h3>

                            <div className="space-y-3 sm:space-y-4 mb-4 sm:mb-6">
                                <div className="flex items-start gap-2 sm:gap-3 p-2.5 sm:p-3 bg-orange-50 rounded-lg">
                                    <MapPin className="w-4 h-4 sm:w-5 sm:h-5 text-orange-600 flex-shrink-0 mt-0.5" />
                                    <div>
                                        <p className="font-semibold text-xs sm:text-sm text-gray-900">Bangalore Tour</p>
                                        <p className="text-[10px] sm:text-xs text-gray-600">Full day</p>
                                    </div>
                                </div>

                                {formData.date && (
                                    <div className="flex items-center gap-2 sm:gap-3 p-2.5 sm:p-3 bg-blue-50 rounded-lg">
                                        <Calendar className="w-4 h-4 sm:w-5 sm:h-5 text-blue-600" />
                                        <div>
                                            <p className="text-xs sm:text-sm font-semibold text-gray-900">
                                                {new Date(formData.date).toLocaleDateString('en-IN', {
                                                    day: 'numeric',
                                                    month: 'short',
                                                    year: 'numeric'
                                                })}
                                            </p>
                                        </div>
                                    </div>
                                )}

                                <div className="flex items-center gap-2 sm:gap-3 p-2.5 sm:p-3 bg-green-50 rounded-lg">
                                    <Users className="w-4 h-4 sm:w-5 sm:h-5 text-green-600" />
                                    <div>
                                        <p className="text-xs sm:text-sm font-semibold text-gray-900">
                                            {totalTravelers} Traveler{totalTravelers !== 1 ? 's' : ''}
                                        </p>
                                    </div>
                                </div>
                            </div>

                            <div className="border-t border-gray-200 pt-3 sm:pt-4 mb-4 sm:mb-6">
                                <div className="space-y-1.5 sm:space-y-2 mb-2 sm:mb-3">
                                    {totalTravelers > 0 && (
                                        <div className="flex justify-between text-xs sm:text-sm">
                                            <span className="text-gray-600">Travelers ({totalTravelers} × ₹2,199)</span>
                                            <span className="font-semibold">₹{basePrice.toLocaleString('en-IN')}</span>
                                        </div>
                                    )}

                                    {appliedCoupon && (
                                        <>
                                            <div className="flex justify-between text-xs sm:text-sm pt-1.5 sm:pt-2 border-t border-gray-100">
                                                <span className="text-gray-600">Subtotal</span>
                                                <span className="font-semibold">₹{basePrice.toLocaleString('en-IN')}</span>
                                            </div>
                                            <div className="flex justify-between text-xs sm:text-sm">
                                                <span className="text-green-600 font-semibold flex items-center gap-1">
                                                    <Tag className="w-3 h-3 sm:w-4 sm:h-4" />
                                                    {appliedCoupon.code} ({appliedCoupon.discount}%)
                                                </span>
                                                <span className="font-semibold text-green-600">-₹{discountAmount.toLocaleString('en-IN')}</span>
                                            </div>
                                        </>
                                    )}
                                </div>

                                <div className="flex justify-between items-center pt-2 sm:pt-3 border-t border-gray-200">
                                    <span className="text-sm sm:text-base lg:text-lg font-bold text-gray-900">Total</span>
                                    <div className="text-right">
                                        {appliedCoupon && (
                                            <div className="text-[10px] sm:text-xs text-gray-400 line-through">
                                                ₹{basePrice.toLocaleString('en-IN')}
                                            </div>
                                        )}
                                        <span className="text-lg sm:text-xl lg:text-2xl font-bold text-orange-600">
                                            ₹{totalPrice.toLocaleString('en-IN')}
                                        </span>
                                        {appliedCoupon && (
                                            <div className="text-[10px] sm:text-xs text-green-600 font-semibold">
                                                Save: ₹{discountAmount.toLocaleString('en-IN')}
                                            </div>
                                        )}
                                    </div>
                                </div>
                            </div>

                            <button
                                onClick={handleSubmit}
                                disabled={isLoading}
                                className="w-full bg-gradient-to-r from-orange-600 to-red-600 text-white py-2.5 sm:py-3 rounded-lg sm:rounded-xl font-semibold text-sm sm:text-base hover:shadow-xl transform hover:-translate-y-0.5 transition-all disabled:opacity-50 disabled:cursor-not-allowed disabled:transform-none flex items-center justify-center gap-2"
                            >
                                {isLoading ? (
                                    <>
                                        <Loader2 className="w-4 h-4 sm:w-5 sm:h-5 animate-spin" />
                                        Processing...
                                    </>
                                ) : (
                                    <>
                                        Proceed to Payment
                                        <IndianRupee className="w-4 h-4 sm:w-5 sm:h-5" />
                                    </>
                                )}
                            </button>

                            <p className="text-[10px] sm:text-xs text-gray-500 text-center mt-3 sm:mt-4">
                                By proceeding, you agree to our T&C
                            </p>

                            <div className="mt-4 sm:mt-6 pt-4 sm:pt-6 border-t border-gray-200">
                                <h4 className="text-xs sm:text-sm font-bold text-gray-900 mb-2 sm:mb-3">What's Included</h4>
                                <div className="space-y-1.5 sm:space-y-2">
                                    {[
                                        'All entry tickets',
                                        'Breakfast included',
                                        'AC transportation',
                                        'Trip coordinator'
                                    ].map((item, idx) => (
                                        <div key={idx} className="flex items-center gap-1.5 sm:gap-2">
                                            <CheckCircle className="w-3 h-3 sm:w-4 sm:h-4 text-green-600 flex-shrink-0" />
                                            <span className="text-[10px] sm:text-xs text-gray-600">{item}</span>
                                        </div>
                                    ))}
                                </div>
                            </div>
                        </div>
                    </div>
                </div>

                {/* Trust Badges */}
                <div className="mt-6 sm:mt-8 grid grid-cols-3 gap-2 sm:gap-4">
                    <div className="bg-white rounded-lg sm:rounded-xl p-2.5 sm:p-4 shadow-md text-center">
                        <CheckCircle className="w-5 h-5 sm:w-6 sm:h-6 lg:w-8 lg:h-8 text-green-600 mx-auto mb-1 sm:mb-2" />
                        <p className="text-[10px] sm:text-xs lg:text-sm font-semibold text-gray-900">Secure</p>
                        <p className="text-[9px] sm:text-[10px] lg:text-xs text-gray-600 hidden sm:block">100% safe</p>
                    </div>
                    <div className="bg-white rounded-lg sm:rounded-xl p-2.5 sm:p-4 shadow-md text-center">
                        <Phone className="w-5 h-5 sm:w-6 sm:h-6 lg:w-8 lg:h-8 text-orange-600 mx-auto mb-1 sm:mb-2" />
                        <p className="text-[10px] sm:text-xs lg:text-sm font-semibold text-gray-900">24/7</p>
                        <p className="text-[9px] sm:text-[10px] lg:text-xs text-gray-600 hidden sm:block">Support</p>
                    </div>
                    <div className="bg-white rounded-lg sm:rounded-xl p-2.5 sm:p-4 shadow-md text-center">
                        <Ticket className="w-5 h-5 sm:w-6 sm:h-6 lg:w-8 lg:h-8 text-blue-600 mx-auto mb-1 sm:mb-2" />
                        <p className="text-[10px] sm:text-xs lg:text-sm font-semibold text-gray-900">Instant</p>
                        <p className="text-[9px] sm:text-[10px] lg:text-xs text-gray-600 hidden sm:block">Confirmation</p>
                    </div>
                </div>
            </div>

            <style jsx>{`
                @keyframes fadeIn {
                    from {
                        opacity: 0;
                        transform: translateY(-10px);
                    }
                    to {
                        opacity: 1;
                        transform: translateY(0);
                    }
                }
                .animate-fadeIn {
                    animation: fadeIn 0.3s ease-out;
                }
            `}</style>
        </div>
    );
};

export default Booking;